import useRequest from "@/lib/request/useRequest";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { useState, useMemo } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import TableV2 from "@/components/table/TableV2";
import { array, string, number, object, InferType } from "yup";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import NumberInputComponent from "../../inputs/NumberInput";
import dayjs from "dayjs";
import { roundTo } from "@/lib/utils/numbers";

const schema = object().shape({
    data: array().of(
        object().shape({
            date: string().required(),
            precipitation: number().min(0).required(),
        })
    ).required(),
});
type FormType = InferType<typeof schema>;

function PrecipitationEdit({
    precipitationData,
    onClose,
}: {
    precipitationData: Array<{
        date: string;
        precipitation: number;
    }>;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const { formatUnit, formatDateMMDDYYYY } = useI18n();
    const { useMutateRequest } = useRequest();
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            data: precipitationData.map((item) => ({
                date: dayjs(item.date).format("YYYY-MM-DD"),
                precipitation: roundTo(item.precipitation, 2),
            })),
        },
        mode: "onBlur",
    });
    const data = form.watch("data");
    const mutatePrecipitation = useMutateRequest({
        url: "/weather/bulk_update",
        method: "put",
        successMessage: t("insights.messages.updatedPrecipitation"),
        errorMessage: t("insights.messages.failedToUpdatePrecipitation"),
    });

    const savePrecipitation = () => {
        mutatePrecipitation.mutate(
            { data },
            {
                onSuccess: () => {
                    onClose();
                },
            }
        );
    };

    const columns: ColumnDef<{
        date: string;
        precipitation: number;
    }>[] = useMemo(() => [
        {
            accessorKey: "date",
            header: t("date"),
            cell: ({ row }) => formatDateMMDDYYYY(row.original.date),
        },
        {
            accessorKey: "precipitation",
            header: t("precipitation"),
            cell: ({ row }) => (
                <NumberInputComponent
                    name={`data.${row.index}.precipitation`}
                    endContent={formatUnit("in", "mm")}
                    size="sm"

                />
            ),
        },
    ], [t, formatDateMMDDYYYY, formatUnit]);

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: "date",
            desc: false,
        },
    ]);

    return (
        <Modal isOpen onClose={onClose} size="md" scrollBehavior="inside">
            <ModalContent>
                <ModalHeader className="flex flex-row justify-center">Precipitation</ModalHeader>
                <ModalBody className="flex flex-col items-center w-full">
                    <FormProvider {...form}>
                        <TableV2
                            data={data}
                            columns={columns}
                            sorting={sorting}
                            setSorting={setSorting}
                        />
                    </FormProvider>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        fullWidth
                        isLoading={mutatePrecipitation.isPending}
                        isDisabled={data.length === 0}
                        onPress={savePrecipitation}
                    >
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                        {t("save")}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default PrecipitationEdit;